import React, {useCallback, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useController} from 'react-hook-form'
import {defineMessages, useIntl} from 'react-intl'
import {Box, createListCollection, Select, useSlotRecipe} from '@chakra-ui/react3'
import Field from '../field'
import {FlagImage} from '../icons'
import {useCountry} from '../../hooks/use-country'
import {useSortedPhoneCountries} from '../../hooks/use-sorted-phone-countries'
import {isE164PhoneNumber, parsePhoneNumber, removeLeadingZeroes} from '../../utils/phone-utils'
import {COUNTRY_MESSAGES, PHONE_COUNTRIES} from '../../constants'
import styles, {phoneCountrySelectRecipe} from './index.recipe'

const getNumericPhoneInputValue = (value) => value.replace(/\D/g, '')
const getCompletePhoneValue = (dialCode, value) => (value ? `${dialCode}${value}` : '')
const getPhoneValueForCountryChange = (localPhoneValue, dialCode) => {
    const numericPhoneValue = getNumericPhoneInputValue(localPhoneValue)
    return getCompletePhoneValue(dialCode, removeLeadingZeroes(numericPhoneValue))
}

const messages = defineMessages({
    label: {defaultMessage: 'Phone Number', id: 'use_profile_fields.label.phone'},
    required: {
        defaultMessage: 'Please enter your phone number.',
        id: 'use_address_fields.error.please_enter_your_phone_number'
    },
    invalid: {
        defaultMessage: 'Please enter a valid phone number',
        id: 'use_address_fields.error.not_correct_format'
    },
    countryPrefix: {
        defaultMessage: 'Country prefix',
        id: 'phone_number_input.label.country_prefix'
    }
})

const PhoneCountryFlag = ({countryCode, flagStyles, loading}) => (
    <Box as="span" aria-hidden="true" css={flagStyles}>
        <FlagImage countryCode={countryCode} loading={loading} />
    </Box>
)

PhoneCountryFlag.propTypes = {
    countryCode: PropTypes.string.isRequired,
    flagStyles: PropTypes.object.isRequired,
    loading: PropTypes.oneOf(['eager', 'lazy'])
}

const PhoneCountrySelector = ({countryCode, dialCode, inputGroupRef, onCountryChange}) => {
    const {formatMessage} = useIntl()
    const phoneCountries = useSortedPhoneCountries()
    const label = formatMessage(messages.countryPrefix)
    const countryItems = useMemo(
        () =>
            phoneCountries.map((phoneCountry) => ({
                ...phoneCountry,
                label: formatMessage(COUNTRY_MESSAGES[phoneCountry.countryCode]),
                value: phoneCountry.countryCode
            })),
        [formatMessage, phoneCountries]
    )
    const collection = useMemo(() => createListCollection({items: countryItems}), [countryItems])
    const recipe = useSlotRecipe({recipe: phoneCountrySelectRecipe})
    const selectStyles = recipe()

    return (
        <Select.Root
            unstyled
            lazyMount
            unmountOnExit
            collection={collection}
            value={[countryCode]}
            css={selectStyles.root}
            loopFocus={true}
            positioning={{
                placement: 'bottom-start',
                sameWidth: true,
                offset: {mainAxis: 0},
                getAnchorRect: () => inputGroupRef.current?.getBoundingClientRect() ?? null
            }}
            onValueChange={({items}) => {
                if (items[0]) onCountryChange(items[0])
            }}
        >
            <Select.HiddenSelect />
            <Select.Label className="sr-only">{label}</Select.Label>
            <Select.Control css={selectStyles.control}>
                <Select.Trigger css={selectStyles.trigger}>
                    <PhoneCountryFlag
                        countryCode={countryCode}
                        flagStyles={selectStyles.countryFlag}
                        loading="eager"
                    />
                    <Box as="span" css={selectStyles.countryDialCode}>
                        {dialCode}
                    </Box>
                    <Select.Indicator css={selectStyles.indicator} />
                </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
                <Select.Content css={selectStyles.content}>
                    {collection.items.map((phoneCountry) => (
                        <Select.Item
                            css={selectStyles.item}
                            item={phoneCountry}
                            key={phoneCountry.countryCode}
                        >
                            <PhoneCountryFlag
                                countryCode={phoneCountry.countryCode}
                                flagStyles={selectStyles.countryFlag}
                            />
                            <Box as="span" css={selectStyles.countryDialCode}>
                                {phoneCountry.dialCode}
                            </Box>
                            <Select.ItemText css={selectStyles.itemText}>
                                {phoneCountry.label}
                            </Select.ItemText>
                        </Select.Item>
                    ))}
                </Select.Content>
            </Select.Positioner>
        </Select.Root>
    )
}

PhoneCountrySelector.propTypes = {
    countryCode: PropTypes.string.isRequired,
    dialCode: PropTypes.string.isRequired,
    inputGroupRef: PropTypes.shape({current: PropTypes.any}).isRequired,
    onCountryChange: PropTypes.func.isRequired
}

const PhoneNumberInput = ({
    name = 'phone',
    control,
    error,
    label,
    helpText,
    required,
    inputProps,
    defaultCountryCode,
    defaultValue,
    rules,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const currentCountry = useCountry()
    const fallbackCountryCode = defaultCountryCode?.toUpperCase() || currentCountry
    const isRequired = required ?? (rules ? Boolean(rules.required) : true)
    const {
        field: {value: fieldValue, onChange: onPhoneChange}
    } = useController({name, control, defaultValue})
    const storedPhone = fieldValue ?? defaultValue
    const storedPhoneRef = useRef(storedPhone)
    const inputGroupRef = useRef(null)
    storedPhoneRef.current = storedPhone
    const phoneParts = parsePhoneNumber(storedPhone, fallbackCountryCode)

    const [selectedCountryCode, setSelectedCountryCode] = useState(() => phoneParts.countryCode)
    const selectedCountry = useMemo(
        () =>
            PHONE_COUNTRIES.find(({countryCode}) => countryCode === selectedCountryCode) ||
            PHONE_COUNTRIES.find(({countryCode}) => countryCode === fallbackCountryCode),
        [fallbackCountryCode, selectedCountryCode]
    )

    const hasUserEditedRef = useRef(false)
    const phoneLabel = label || formatMessage(messages.label)
    const getLocalPhoneValue = useCallback(
        (value) => {
            const localPhoneValue = parsePhoneNumber(value, selectedCountry.countryCode).value
            return (
                localPhoneValue ||
                // Preserves existing unparseable values so users can correct them.
                (typeof value === 'string' && value && !isE164PhoneNumber(value) ? value : '')
            )
        },
        [selectedCountry.countryCode]
    )
    const parsePhoneInputValue = (value) => {
        hasUserEditedRef.current = true
        return getCompletePhoneValue(selectedCountry.dialCode, getNumericPhoneInputValue(value))
    }
    const phoneInputProps = ({value, onChange}) => ({
        autoComplete: 'tel',
        __css: styles.input,
        ...inputProps,
        onBlur: (event) => {
            inputProps?.onBlur?.(event)
            if (hasUserEditedRef.current) {
                onChange(
                    getCompletePhoneValue(
                        selectedCountry.dialCode,
                        removeLeadingZeroes(getLocalPhoneValue(value))
                    )
                )
            }
        }
    })
    const handleCountryChange = useCallback(
        (phoneCountry) => {
            setSelectedCountryCode(phoneCountry.countryCode)
            onPhoneChange(
                getPhoneValueForCountryChange(
                    getLocalPhoneValue(storedPhoneRef.current),
                    phoneCountry.dialCode
                )
            )
        },
        [getLocalPhoneValue, onPhoneChange]
    )

    return (
        <Field
            {...props}
            name={name}
            label={phoneLabel}
            type="tel"
            control={control}
            error={error}
            placeholder={selectedCountry.placeholder}
            helpText={helpText}
            defaultValue={defaultValue}
            formatDisplayValue={getLocalPhoneValue}
            parseInputValue={parsePhoneInputValue}
            inputGroupProps={{__css: styles.inputGroup, ref: inputGroupRef}}
            inputProps={phoneInputProps}
            leftElement={
                <PhoneCountrySelector
                    countryCode={selectedCountry.countryCode}
                    dialCode={selectedCountry.dialCode}
                    inputGroupRef={inputGroupRef}
                    onCountryChange={handleCountryChange}
                />
            }
            rules={{
                ...rules,
                required: isRequired ? rules?.required || formatMessage(messages.required) : null,
                validate: {
                    ...(typeof rules?.validate === 'function' ? {custom: rules.validate} : {}),
                    ...(typeof rules?.validate === 'object' ? rules.validate : {}),
                    e164: (value) =>
                        !value || isE164PhoneNumber(value) || formatMessage(messages.invalid)
                }
            }}
        />
    )
}

PhoneNumberInput.propTypes = {
    name: PropTypes.string,
    control: PropTypes.object.isRequired,
    error: PropTypes.object,
    label: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    inputProps: PropTypes.object,
    defaultCountryCode: PropTypes.string,
    defaultValue: PropTypes.string,
    rules: PropTypes.object
}

export default PhoneNumberInput
